
import { Component, ChangeDetectionStrategy, inject, signal, OnInit, effect, computed } from '@angular/core';
import { DatePipe, DecimalPipe, PercentPipe } from '@angular/common'; 
import { FormsModule } from '@angular/forms';
import { DataService } from '../../services/data.service';
import { AuthService } from '../../services/auth.service';
import { ReportingService } from '../../services/reporting.service'; 
import { BestsellerItem, SalesSummaryReport, CategorySalesReport, EmployeeSalesReport, PaymentMethodSalesReport, DiscountReport, TaxReport, ShiftReport, AppUser, ClinicalSymptomStats, DoctorPerformanceStats, ReferralStatItem } from '../../models/index';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { TranslationService } from '../../services/translation.service';
import { NotificationService } from '../../services/notification.service';
import { ExportService } from '../../services/export.service';

type LoadingState = 'loading' | 'loaded' | 'error';
type DatePreset = 'today' | '7d' | '30d';
type ReportType = 'clinical-stats' | 'referral-stats' | 'sales-summary' | 'sales-by-product' | 'sales-by-category' | 'sales-by-employee' | 'sales-by-payment' | 'discounts' | 'taxes' | 'shifts';

@Component({
  selector: 'app-reports',
  standalone: true,
  templateUrl: './reports.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, DatePipe, DecimalPipe, PercentPipe, FormsModule], 
})
export class ReportsComponent implements OnInit {
  private dataService = inject(DataService);
  private authService = inject(AuthService);
  private reportingService = inject(ReportingService);
  private translationService = inject(TranslationService);
  private notificationService = inject(NotificationService);
  private exportService = inject(ExportService);

  // === State Signals ===
  reportType = signal<ReportType>('clinical-stats'); // Default to clinical for better UX
  loadingState = signal<LoadingState>('loading');
  loadingError = signal<string | null>(null);
  
  // Data Signals for each report type
  salesSummary = signal<SalesSummaryReport | null>(null);
  dailyBreakdown = signal<any[]>([]); // New signal to hold daily data for export
  bestsellers = signal<BestsellerItem[]>([]);
  categorySales = signal<CategorySalesReport[]>([]);
  employeeSales = signal<EmployeeSalesReport[]>([]);
  paymentMethodSales = signal<PaymentMethodSalesReport[]>([]);
  discounts = signal<DiscountReport[]>([]);
  taxes = signal<TaxReport[]>([]);
  shifts = signal<ShiftReport[]>([]);
  
  // Clinical Data
  symptomStats = signal<ClinicalSymptomStats[]>([]);
  doctorStats = signal<DoctorPerformanceStats[]>([]);
  referralStats = signal<ReferralStatItem[]>([]); 

  // Filter States
  employees = signal<AppUser[]>([]);
  selectedCashierId = signal<string>('all');
  activePreset = signal<DatePreset>('30d');
  dateRange = signal<{ start: Date, end: Date }>({ start: new Date(), end: new Date() });

  hasAccess = this.authService.hasFeature('reporting');
  
  // Computed Props for Referral Stats
  totalReferrals = computed(() => this.referralStats().reduce((acc, curr) => acc + curr.totalRequests, 0));
  totalApprovedReferrals = computed(() => this.referralStats().reduce((acc, curr) => acc + curr.approvedCount, 0));
  topReferringPartner = computed(() => {
     if (this.referralStats().length === 0) return '-';
     return this.referralStats()[0].partnerName;
  });


  constructor() {
    // Re-load data whenever a filter or report type changes
    effect(() => {
        if (this.hasAccess()) {
            this.loadData();
        }
    });
  }

  ngOnInit(): void {
    if (this.hasAccess()) {
        this.loadEmployees();
        this.setDateRange('30d');
    }
  }

  async loadEmployees(): Promise<void> {
    try {
        const users = await this.dataService.getUsers();
        this.employees.set(users.filter(u => u.role === 'cashier' || u.role === 'admin' || u.role === 'owner'));
    } catch(e) {
        console.error("Failed to load employees for filter", e);
    }
  }

  switchReport(type: ReportType): void {
    this.reportType.set(type);
  }

  setDateRange(preset: DatePreset): void {
    this.activePreset.set(preset);
    const end = new Date();
    const start = new Date();
    
    switch(preset) {
        case 'today':
            start.setHours(0, 0, 0, 0);
            break;
        case '7d':
            start.setDate(end.getDate() - 6);
            start.setHours(0, 0, 0, 0);
            break;
        case '30d':
            start.setDate(end.getDate() - 29);
            start.setHours(0, 0, 0, 0);
            break;
    }
    this.dateRange.set({ start, end });
  }

  async loadData(): Promise<void> {
    this.loadingState.set('loading');
    this.loadingError.set(null);
    try {
      const { start, end } = this.dateRange();
      const cashierId = this.selectedCashierId() === 'all' ? undefined : this.selectedCashierId();

      switch (this.reportType()) {
        case 'clinical-stats':
           const [symptoms, doctors] = await Promise.all([
               this.reportingService.getClinicalSymptomStats(start.toISOString(), end.toISOString()),
               this.reportingService.getDoctorPerformance(start.toISOString(), end.toISOString())
           ]);
           this.symptomStats.set(symptoms);
           this.doctorStats.set(doctors);
           break;
        case 'referral-stats':
           const referrals = await this.reportingService.getClinicReferralStats(start.toISOString(), end.toISOString());
           this.referralStats.set(referrals);
           break;
        case 'sales-summary':
          // Need to fetch dashboard summary to get daily breakdown for export
          const dashData = await this.dataService.getSalesSummaryData(start.toISOString(), end.toISOString());
          this.dailyBreakdown.set(dashData.dailyBreakdown || []);
          this.salesSummary.set(await this.reportingService.getSalesSummaryReport(start.toISOString(), end.toISOString(), cashierId));
          break;
        case 'sales-by-product':
          this.bestsellers.set(await this.reportingService.getBestsellersReport(start.toISOString(), end.toISOString(), cashierId));
          break;
        case 'sales-by-category':
          this.categorySales.set(await this.reportingService.getCategorySalesReport(start.toISOString(), end.toISOString(), cashierId));
          break;
        case 'sales-by-employee':
          this.employeeSales.set(await this.reportingService.getEmployeeSalesReport(start.toISOString(), end.toISOString(), cashierId));
          break;
        case 'sales-by-payment':
          this.paymentMethodSales.set(await this.reportingService.getPaymentMethodSalesReport(start.toISOString(), end.toISOString(), cashierId));
          break;
        case 'discounts':
          this.discounts.set(await this.reportingService.getDiscountsReport(start.toISOString(), end.toISOString(), cashierId));
          break;
        case 'taxes':
          this.taxes.set(await this.reportingService.getTaxesReport(start.toISOString(), end.toISOString(), cashierId));
          break;
        case 'shifts':
          this.shifts.set(await this.reportingService.getShiftsReport(start.toISOString(), end.toISOString(), cashierId));
          break;
      }
      this.loadingState.set('loaded');
    } catch (e) {
      this.loadingState.set('error');
      const message = (e as any).message || 'An unknown error occurred while fetching report data.';
      this.loadingError.set(message);
      console.error(`Failed to load report '${this.reportType()}':`, e);
    }
  }

  exportToCsv(): void {
    const type = this.reportType();
    const dateStr = this.dateRange().start.toISOString().split('T')[0];
    let dataToExport: any[] = [];
    let filename = `Report_${type}_${dateStr}.csv`;

    // Select data based on current tab
    switch (type) {
        case 'clinical-stats':
            // Prioritize Doctor Performance as tabular data
            if (this.doctorStats().length > 0) {
                dataToExport = this.doctorStats().map(d => ({
                    'Doctor Name': d.doctorName,
                    'Patients Seen': d.patientCount,
                    'Prescriptions Issued': d.prescriptionsIssued
                }));
                filename = `Doctor_Performance_${dateStr}.csv`;
            } else if (this.symptomStats().length > 0) {
                // Fallback to symptoms if no doctor stats
                dataToExport = this.symptomStats().map(s => ({
                   'Symptom': s.symptom,
                   'Count': s.count,
                   'Percentage': s.percentage + '%'
                }));
                filename = `Symptom_Stats_${dateStr}.csv`;
            }
            break;

        case 'referral-stats':
            dataToExport = this.referralStats().map(r => ({
                'Partner Name': r.partnerName,
                'Partner ID': r.partnerId,
                'Total Requests': r.totalRequests,
                'Approved': r.approvedCount,
                'Rejected': r.rejectedCount,
                'Pending': r.pendingCount,
                'Last Activity': r.lastActivity ? new Date(r.lastActivity).toLocaleDateString() : '-'
            }));
            break;

        case 'sales-summary':
            if (this.dailyBreakdown().length > 0) {
                 dataToExport = this.dailyBreakdown().map(d => ({
                    'Date': new Date(d.date).toLocaleDateString(),
                    'Gross Sales': d.grossSales,
                    'Net Sales': d.netSales
                 }));
            } else if (this.salesSummary()) {
                 // Single row summary if no daily data
                 const s = this.salesSummary()!;
                 dataToExport = [{
                     'Gross Sales': s.grossSales,
                     'Net Sales': s.netSales,
                     'Discounts': s.discounts,
                     'Transactions': s.transactionCount,
                     'Average Sale': s.averageSale
                 }];
            }
            break;

        case 'sales-by-product':
            dataToExport = this.bestsellers().map(b => ({
                'Product Name': b.productName,
                'SKU': b.sku,
                'Category': b.category,
                'Quantity Sold': b.quantitySold,
                'Gross Sales': b.grossSales
            }));
            break;
            
        case 'sales-by-category':
            dataToExport = this.categorySales().map(c => ({
                'Category': c.category,
                'Transactions': c.transactionCount,
                'Items Sold': c.itemsSold,
                'Gross Sales': c.grossSales
            }));
            break;

        case 'sales-by-employee':
            dataToExport = this.employeeSales().map(e => ({
                'Employee': e.employeeName,
                'Transactions': e.transactionCount,
                'Net Sales': e.netSales,
                'Avg Sale': e.averageSale
            }));
            break;

        case 'sales-by-payment':
             dataToExport = this.paymentMethodSales().map(p => ({
                'Payment Method': p.paymentMethod,
                'Transactions': p.transactionCount,
                'Total Value': p.totalValue
            }));
            break;

        case 'shifts':
             dataToExport = this.shifts().map(s => ({
                'Cashier': s.cashierName,
                'Opened At': new Date(s.openedAt).toLocaleString(),
                'Closed At': s.closedAt ? new Date(s.closedAt).toLocaleString() : 'Open',
                'Open Amount': s.openAmount,
                'Close Amount': s.closeAmount,
                'Expected': s.expectedAmount,
                'Variance': s.variance
            }));
            break;
            
        case 'taxes':
             dataToExport = this.taxes().map(t => ({
                'Date': new Date(t.date).toLocaleDateString(),
                'Taxable Amount': t.taxableAmount,
                'Tax Amount': t.taxAmount
            }));
            break;
    }

    if (dataToExport.length === 0) {
        this.notificationService.showInfo('ไม่มีข้อมูลสำหรับส่งออกในช่วงเวลานี้ (No data to export)');
        return;
    }

    this.exportService.exportToCsv(dataToExport, filename);
  }
}
